import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  RefreshCw, X, Undo2, CalendarClock, PackageOpen, Play, Ban, AlertTriangle,
  Plus, CreditCard, Mail, Search as SearchIcon,
} from 'lucide-react';
import {
  RentalAgreement, RentalEvent, RentalInvoiceRef, RENTAL_STATUS_CLASSES, RENTAL_STATUS_LABEL,
  cancelRentalAgreement, cancelPendingRental, endRentalAgreement,
  fetchCustomersWithCards, listRentalAgreements, listRentalEvents, listRentalInvoices,
  rentalRateText, runRentalBilling, sendRentalPayLink,
} from '../../lib/rentals';
import { quoteContainerRefund, recordContainerReturn } from '../../lib/customerOrders';
import { RefundDestination, RefundOutcome, describeRefundOutcome } from '../../lib/refunds';
import RefundSheet from '../refunds/RefundSheet';
import { Customer, getCustomer } from '../../lib/customers';
import { supabase } from '../../lib/supabase';
import { SidePanel } from './SidePanel';
import StartRentalModal from './StartRentalModal';
import RecordPaymentModal from '../RecordPaymentModal';
import BookingCustomerInfo from '../booking/BookingCustomerInfo';
import CustomerDetailsModal from '../CustomerDetailsModal';

const money = (cents: number) => `$${(cents / 100).toFixed(2)}`;
const shortDate = (d: string | null) =>
  d ? new Date(d.includes('T') ? d : `${d}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

function customerName(a: RentalAgreement): string {
  const n = `${a.customer?.first_name || ''} ${a.customer?.last_name || ''}`.trim();
  return n || a.customer?.email || 'Customer';
}

function StatusBadge({ a }: { a: RentalAgreement }) {
  // Staff-started pending rentals wait on payment, not an order delivery.
  const label = a.status === 'pending' && !a.order_id ? 'Pending payment' : RENTAL_STATUS_LABEL[a.status];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${RENTAL_STATUS_CLASSES[a.status]}`}>
      {label}
      {a.cancel_at_period_end && (a.status === 'active' || a.status === 'past_due') && ' · ends soon'}
    </span>
  );
}

/** Billing-state strip along the bottom — mirrors the order card's invoice strip. */
function rentalStrip(a: RentalAgreement): { text: string; cls: string; icon: 'calendar' | 'alert' } {
  if (a.status === 'past_due') {
    return {
      text: a.failed_attempts > 0
        ? `Payment outstanding · ${a.failed_attempts} failed charge${a.failed_attempts > 1 ? 's' : ''} — retrying automatically`
        : 'Payment outstanding',
      cls: 'bg-red-50 text-red-700 border-red-100',
      icon: 'alert',
    };
  }
  if (a.status === 'pending') {
    return a.order_id
      ? { text: 'Starts on delivery — first period bills on the delivery invoice', cls: 'bg-gray-50 text-gray-500 border-gray-100', icon: 'calendar' }
      : { text: 'Starts when the first invoice is paid', cls: 'bg-orange-50 text-orange-700 border-orange-100', icon: 'calendar' };
  }
  if (a.status === 'active') {
    return {
      text: `Next bill ${shortDate(a.next_bill_date)}${a.cancel_at_period_end ? ' · ends at period end' : ''}`,
      cls: 'bg-green-50 text-green-700 border-green-100',
      icon: 'calendar',
    };
  }
  return {
    text: a.ended_at ? `Ended ${shortDate(a.ended_at)}` : 'Ended',
    cls: 'bg-gray-50 text-gray-400 border-gray-100',
    icon: 'calendar',
  };
}

/** One rental row — shared by the Rentals tab and the customer card.
 *  Same layout as the standard order card (OrderRow): name + interval badge
 *  top-left, status top-right, customer + amount in the middle, and a
 *  billing-state strip along the bottom. */
export function RentalRow({ a, onClick, hideCustomer, noCard }: {
  a: RentalAgreement;
  onClick: () => void;
  hideCustomer?: boolean;
  /** Customer has no saved card — surfaced so staff chase cards early. */
  noCard?: boolean;
}) {
  const strip = rentalStrip(a);
  const per = a.billing_interval === 'week' ? 'wk' : 'mo';
  return (
    <button
      onClick={onClick}
      className="w-full text-left bg-white rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-sm transition-all overflow-hidden"
    >
      <div className="px-4 pt-3 pb-2.5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[13px] font-bold text-gray-900 truncate">
              {a.quantity} × {a.item?.name || 'Item'}
            </span>
            {a.rental_number && (
              <span className="flex-shrink-0 text-[11px] font-semibold text-gray-400">{a.rental_number}</span>
            )}
            <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-indigo-100 text-indigo-700">
              <RefreshCw className="w-3 h-3" />
              {a.billing_interval === 'week' ? 'Weekly' : 'Monthly'}
            </span>
          </div>
          <span className="flex items-center gap-1.5 flex-shrink-0">
            {noCard && a.status !== 'ended' && a.status !== 'canceled' && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-orange-50 text-orange-700 border-orange-200">
                No card on file
              </span>
            )}
            <StatusBadge a={a} />
          </span>
        </div>
        <div className="flex items-end justify-between gap-3 mt-1.5">
          <div className="min-w-0">
            {!hideCustomer && (
              <p className="text-[13px] font-medium text-gray-800 truncate">{customerName(a)}</p>
            )}
            <p className="text-[11px] text-gray-400">
              {a.started_at ? `Started ${shortDate(a.started_at)}` : `Created ${shortDate(a.created_at)}`}
              {' · '}{rentalRateText(a)}
            </p>
          </div>
          <p className="text-[15px] font-bold text-gray-900 flex-shrink-0">
            {money(a.rate_cents * a.quantity)}<span className="text-[11px] font-semibold text-gray-400">/{per}</span>
          </p>
        </div>
      </div>
      <div className={`px-4 py-1.5 border-t text-[11px] font-semibold flex items-center gap-1.5 ${strip.cls}`}>
        {strip.icon === 'alert'
          ? <AlertTriangle className="w-3 h-3 flex-shrink-0" />
          : <CalendarClock className="w-3 h-3 flex-shrink-0" />}
        <span className="truncate">{strip.text}</span>
      </div>
    </button>
  );
}

function invoiceBadge(inv: RentalInvoiceRef): { label: string; cls: string } {
  if (inv.status === 'void') return { label: 'Void', cls: 'bg-gray-100 text-gray-500' };
  if (inv.status === 'paid') return { label: 'Paid', cls: 'bg-emerald-50 text-emerald-700' };
  if (inv.status === 'partially_paid' || (inv.amount_paid_cents > 0 && inv.amount_due_cents > 0)) {
    return { label: 'Partial', cls: 'bg-amber-50 text-amber-700' };
  }
  if (inv.status === 'draft') return { label: 'Draft', cls: 'bg-gray-100 text-gray-500' };
  return { label: 'Unpaid', cls: 'bg-red-50 text-red-600' };
}

/** Slide-in detail panel: status, billing info, return/cancel actions, event log. */
export function RentalDetailPanel({ agreement, onClose, onChanged, zBase = 100060 }: {
  agreement: RentalAgreement;
  onClose: () => void;
  onChanged: () => void;
  zBase?: number;
}) {
  const [a, setA] = useState(agreement);
  const [events, setEvents] = useState<RentalEvent[]>([]);
  const [busy, setBusy] = useState(false);
  const [showReturn, setShowReturn] = useState(false);
  const [returnQty, setReturnQty] = useState(agreement.quantity);
  const [returnReason, setReturnReason] = useState('');
  const [endAll, setEndAll] = useState(false);
  // Deposit refund follow-up after a return: the equipment is back, now the
  // security deposit needs a destination (wallet / cash / original card).
  const [depositPrompt, setDepositPrompt] = useState<{ qty: number; refundCents: number } | null>(null);
  const [showRefundSheet, setShowRefundSheet] = useState(false);
  // Last invoice, when it still owes money — drives collect/pay-link actions.
  const [openInvoice, setOpenInvoice] = useState<{ id: string; number: string; totalCents: number; dueCents: number } | null>(null);
  const [showPay, setShowPay] = useState(false);
  const [sendingLink, setSendingLink] = useState(false);
  // Full customer record for the standard customer card (call/text/email + details).
  const [fullCustomer, setFullCustomer] = useState<Customer | null>(null);
  const [showCustomerDetails, setShowCustomerDetails] = useState(false);

  const loadFullCustomer = useCallback(async () => {
    try {
      setFullCustomer(await getCustomer(agreement.customer_id));
    } catch { /* card simply not shown */ }
  }, [agreement.customer_id]);
  useEffect(() => { loadFullCustomer(); }, [loadFullCustomer]);

  // Every invoice that ever billed this agreement (tagged line items).
  const [invoiceHistory, setInvoiceHistory] = useState<RentalInvoiceRef[]>([]);

  useEffect(() => { setA(agreement); setReturnQty(agreement.quantity); }, [agreement]);
  useEffect(() => {
    listRentalEvents(agreement.id).then(setEvents).catch(() => {});
    listRentalInvoices(agreement.id).then(setInvoiceHistory).catch(() => {});
  }, [agreement.id]);

  const loadOpenInvoice = useCallback(async (invoiceId: string | null) => {
    if (!invoiceId) { setOpenInvoice(null); return; }
    const { data } = await supabase
      .from('invoices')
      .select('id, invoice_number, status, total_cents, amount_due_cents')
      .eq('id', invoiceId)
      .maybeSingle();
    if (data && data.status !== 'paid' && data.status !== 'void' && Number(data.amount_due_cents) > 0) {
      setOpenInvoice({
        id: data.id as string,
        number: String(data.invoice_number ?? ''),
        totalCents: Number(data.total_cents) || 0,
        dueCents: Number(data.amount_due_cents) || 0,
      });
    } else {
      setOpenInvoice(null);
    }
  }, []);

  useEffect(() => { loadOpenInvoice(agreement.last_invoice_id); }, [agreement.last_invoice_id, loadOpenInvoice]);

  const refresh = useCallback(async () => {
    onChanged();
    try { setEvents(await listRentalEvents(a.id)); } catch { /* non-critical */ }
    try { await loadOpenInvoice(a.last_invoice_id); } catch { /* non-critical */ }
    try { setInvoiceHistory(await listRentalInvoices(a.id)); } catch { /* non-critical */ }
  }, [a.id, a.last_invoice_id, onChanged, loadOpenInvoice]);

  const emailPayLink = async () => {
    if (!openInvoice) return;
    setSendingLink(true);
    try {
      const res = await sendRentalPayLink(a.business_id, openInvoice.id);
      toast.success(`Payment link sent to ${res.to}`);
      await refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to send payment link');
    } finally { setSendingLink(false); }
  };

  const cancelPending = async () => {
    if (!confirm('Cancel this rental? The reserved stock goes back, the unpaid first invoice is voided, and nothing is charged.')) return;
    setBusy(true);
    try {
      await cancelPendingRental(a.id);
      toast.success('Rental cancelled — invoice voided');
      onChanged();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to cancel rental');
    } finally { setBusy(false); }
  };

  const isLive = a.status === 'active' || a.status === 'past_due';

  const toggleCancel = async () => {
    setBusy(true);
    try {
      await cancelRentalAgreement(a.id, !a.cancel_at_period_end);
      setA(prev => ({ ...prev, cancel_at_period_end: !prev.cancel_at_period_end }));
      toast.success(a.cancel_at_period_end ? 'Cancellation revoked — billing continues' : 'Cancellation scheduled for period end');
      await refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update rental');
    } finally { setBusy(false); }
  };

  const submitReturn = async () => {
    if (returnQty <= 0 && !endAll) { toast.error('Enter how many units came back'); return; }
    setBusy(true);
    try {
      const res = await endRentalAgreement({
        agreementId: a.id,
        returned: returnQty,
        reason: returnReason.trim() || undefined,
        endAll,
      });
      if (res.status === 'ended') {
        toast.success(res.still_out > 0
          ? `Rental ended — ${res.still_out} unit${res.still_out > 1 ? 's' : ''} written off`
          : 'Rental ended — all equipment back in stock');
      } else {
        toast.success(`${res.returned} returned — billing continues on ${res.still_out}`);
      }
      setShowReturn(false);
      setA(prev => ({
        ...prev,
        quantity: res.still_out,
        returned_quantity: prev.returned_quantity + res.returned,
        status: res.status as RentalAgreement['status'],
      }));
      await refresh();

      // Item carries a deposit → quote the refund for the returned units and
      // prompt for a destination instead of leaving it for staff to remember.
      if (res.returned > 0 && (a.item?.deposit_cents || 0) > 0) {
        try {
          const quote = await quoteContainerRefund({
            businessId: a.business_id,
            customerId: a.customer_id,
            stockItemId: a.stock_item_id,
            quantity: res.returned,
          });
          const qty = Math.min(res.returned, quote.held_quantity);
          if (qty > 0 && quote.refund_cents > 0) {
            setDepositPrompt({ qty, refundCents: quote.refund_cents });
          }
        } catch { /* no lots to refund — nothing to prompt */ }
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to record return');
    } finally { setBusy(false); }
  };

  // Deposit refund after a rental return — the shared refund sheet picks the
  // destination; the container-return RPC prices it from the lots and routes
  // it through the refund engine.
  const refundDeposit = async (
    destination: RefundDestination,
    reference: string | null
  ): Promise<RefundOutcome | null> => {
    if (!depositPrompt) return null;
    const res = await recordContainerReturn({
      businessId: a.business_id,
      customerId: a.customer_id,
      stockItemId: a.stock_item_id,
      returned: depositPrompt.qty,
      lost: 0,
      refundMethod: destination,
      notes: [`Rental ended — deposit refund (${a.item?.name || 'rental'})`, reference ? `Ref: ${reference}` : '']
        .filter(Boolean)
        .join(' — '),
    });
    return {
      groupId: res.refund_group_id ?? '',
      destination,
      amountCents: res.refund_cents,
      cardCents: res.card_refunded_cents ?? 0,
      walletCents: res.wallet_credited_cents ?? 0,
      cashCents: res.cash_refunded_cents ?? 0,
    };
  };

  return (
    <SidePanel onClose={onClose} width="md:w-[520px]" zIndex={zBase}>
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
        <div>
          <h3 className="text-base font-bold text-gray-900">{a.quantity} × {a.item?.name || 'Rental'}</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {a.rental_number ? `${a.rental_number} · ` : ''}{customerName(a)}
          </p>
        </div>
        <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500">
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        {/* Standard customer card — tap for details, quick call/text/email */}
        {fullCustomer && (
          <BookingCustomerInfo
            customer={fullCustomer}
            businessId={a.business_id}
            onOpenCustomerDetails={() => setShowCustomerDetails(true)}
            canEdit={false}
          />
        )}

        {/* Summary card */}
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-gray-500">Status</span>
            <StatusBadge a={a} />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-500">Rate</span>
            <span className="font-semibold text-gray-900">
              {money(a.rate_cents)}/{a.billing_interval === 'week' ? 'week' : 'month'} per unit
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-500">Recurring total</span>
            <span className="font-semibold text-gray-900">{money(a.rate_cents * a.quantity)}/{a.billing_interval === 'week' ? 'wk' : 'mo'}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-500">Started</span>
            <span className="text-gray-900">{shortDate(a.started_at)}</span>
          </div>
          {isLive && (
            <div className="flex items-center justify-between">
              <span className="text-gray-500">Next bill</span>
              <span className="text-gray-900 font-medium">
                {a.cancel_at_period_end ? `Ends ${shortDate(a.next_bill_date)} — no further billing` : shortDate(a.next_bill_date)}
              </span>
            </div>
          )}
          {a.returned_quantity > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-gray-500">Returned so far</span>
              <span className="text-gray-900">{a.returned_quantity}</span>
            </div>
          )}
          {a.status === 'ended' && a.end_reason && (
            <div className="flex items-center justify-between">
              <span className="text-gray-500">End reason</span>
              <span className="text-gray-900">{a.end_reason}</span>
            </div>
          )}
        </div>

        {a.status === 'pending' && (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3.5 py-2.5">
            {a.order_id
              ? <>Starts when the order is delivered &amp; invoiced — the first period bills on the order invoice and equipment leaves stock then.</>
              : <>Starts the moment invoice #{openInvoice?.number || '…'} is paid — collect the card payment below, or cancel if it fell through.</>}
          </div>
        )}

        {/* Open first/latest invoice: collect payment or send the pay link */}
        {openInvoice && (a.status === 'pending' ? !a.order_id : isLive) && (
          <div className="border border-gray-200 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="font-semibold text-gray-900">Invoice #{openInvoice.number} unpaid</span>
              <span className="font-bold text-gray-900">{money(openInvoice.dueCents)} due</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setShowPay(true)}
                disabled={busy || sendingLink}
                className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-gray-900 text-white text-xs font-semibold disabled:opacity-50"
              >
                <CreditCard className="h-3.5 w-3.5" /> Collect payment
              </button>
              <button
                onClick={emailPayLink}
                disabled={busy || sendingLink}
                className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border border-gray-300 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <Mail className="h-3.5 w-3.5" /> {sendingLink ? 'Sending…' : 'Email pay link'}
              </button>
            </div>
            {a.status === 'pending' && !a.order_id && (
              <button
                onClick={cancelPending}
                disabled={busy}
                className="w-full text-[11px] text-red-600 underline underline-offset-2 disabled:opacity-50"
              >
                Cancel this rental — void the invoice, nothing charged
              </button>
            )}
            <p className="text-[11px] text-gray-400">
              Paying by card (here or through the link) saves the card on file, so future cycles charge automatically.
            </p>
          </div>
        )}

        {/* Deposit refund prompt — shown right after a return so it can't be missed */}
        {depositPrompt && (
          <div className="border border-amber-300 bg-amber-50 rounded-xl p-4 space-y-3">
            <p className="text-sm font-semibold text-gray-900">
              Refund the deposit — {money(depositPrompt.refundCents)} for {depositPrompt.qty} container{depositPrompt.qty > 1 ? 's' : ''}
            </p>
            <p className="text-xs text-gray-600">
              Priced from the deposits this customer actually paid.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setShowRefundSheet(true)}
                className="flex-1 px-3 py-2 rounded-lg bg-gray-900 text-white text-xs font-semibold"
              >
                Refund {money(depositPrompt.refundCents)}…
              </button>
              <button
                onClick={() => setDepositPrompt(null)}
                className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-xs font-semibold text-gray-700"
              >
                Later
              </button>
            </div>
            <p className="text-[11px] text-gray-500">
              Skipping leaves the deposit on the customer's Containers card to refund later.
            </p>
            {showRefundSheet && (
              <RefundSheet
                open
                onClose={() => setShowRefundSheet(false)}
                businessId={a.business_id}
                customerId={a.customer_id}
                amountCents={depositPrompt.refundCents}
                title={`Refund ${money(depositPrompt.refundCents)} deposit`}
                description={`${depositPrompt.qty} container${depositPrompt.qty > 1 ? 's' : ''} back from ${a.item?.name || 'rental'}`}
                destinations={['card', 'wallet', 'cash', 'e_transfer']}
                onConfirm={({ destination, reference }) => refundDeposit(destination, reference)}
                onDone={async outcome => {
                  if (outcome) {
                    toast.success(`Deposit refunded — ${describeRefundOutcome(outcome)}`);
                    setDepositPrompt(null);
                    await refresh();
                  }
                }}
              />
            )}
          </div>
        )}

        {/* Actions */}
        {isLive && (
          <div className="space-y-2">
            {!showReturn ? (
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setShowReturn(true)}
                  disabled={busy}
                  className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  <Undo2 className="h-4 w-4" /> Record return
                </button>
                <button
                  onClick={toggleCancel}
                  disabled={busy}
                  className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-semibold disabled:opacity-50 ${
                    a.cancel_at_period_end
                      ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'
                      : 'border-red-200 text-red-600 hover:bg-red-50'
                  }`}
                >
                  {a.cancel_at_period_end ? <><Play className="h-4 w-4" /> Resume billing</> : <><Ban className="h-4 w-4" /> Cancel at period end</>}
                </button>
              </div>
            ) : (
              <div className="border border-gray-200 rounded-xl p-4 space-y-3">
                <p className="text-sm font-semibold text-gray-900">Record equipment return</p>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Units coming back (of {a.quantity} out)</label>
                  <input
                    type="number" min={0} max={a.quantity} value={returnQty}
                    onChange={(e) => setReturnQty(Math.max(0, Math.min(a.quantity, parseInt(e.target.value) || 0)))}
                    className="w-28 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Reason / note (optional)</label>
                  <input
                    type="text" value={returnReason} onChange={(e) => setReturnReason(e.target.value)}
                    placeholder="e.g. customer canceled service"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                {returnQty < a.quantity && (
                  <label className="flex items-start gap-2 text-xs text-gray-600">
                    <input type="checkbox" checked={endAll} onChange={(e) => setEndAll(e.target.checked)} className="mt-0.5 rounded" />
                    <span>
                      End the rental anyway — write off the {a.quantity - returnQty} unit{a.quantity - returnQty > 1 ? 's' : ''} still out
                      (they will not come back into stock and billing stops).
                    </span>
                  </label>
                )}
                <p className="text-[11px] text-gray-400">
                  Returned units go back into inventory. If fewer than {a.quantity} come back and you don't end the rental, billing continues on the remainder. Container deposits refund from the customer's Containers card.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={submitReturn} disabled={busy}
                    className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-50"
                  >
                    {busy ? 'Saving…' : returnQty >= a.quantity || endAll ? 'Return & end rental' : `Return ${returnQty}`}
                  </button>
                  <button
                    onClick={() => { setShowReturn(false); setEndAll(false); }}
                    className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-600 hover:bg-gray-50"
                  >
                    Back
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Invoice history — every invoice that billed this rental */}
        {invoiceHistory.length > 0 && (
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Invoices</p>
            <div className="border border-gray-200 rounded-xl divide-y divide-gray-100 overflow-hidden">
              {invoiceHistory.map(inv => {
                const b = invoiceBadge(inv);
                return (
                  <div key={inv.invoice_id} className="flex items-center justify-between px-3.5 py-2.5 bg-white">
                    <div className="min-w-0">
                      <p className="text-[13px] font-semibold text-gray-900">Invoice #{inv.invoice_number || '—'}</p>
                      <p className="text-[11px] text-gray-400">{shortDate(inv.date)}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-[13px] font-bold text-gray-900">{money(inv.total_cents)}</p>
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[11px] font-semibold ${b.cls}`}>{b.label}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Event log */}
        <div>
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">History</p>
          {events.length === 0 ? (
            <p className="text-xs text-gray-400">No events yet.</p>
          ) : (
            <div className="space-y-2.5">
              {events.map(ev => (
                <div key={ev.id} className="flex gap-2.5">
                  <span className={`mt-1.5 h-2 w-2 rounded-full flex-shrink-0 ${
                    ev.event_type.includes('fail') || ev.event_type === 'past_due' ? 'bg-red-400'
                      : ev.event_type === 'billed' || ev.event_type === 'activated' ? 'bg-emerald-400'
                        : 'bg-gray-300'
                  }`} />
                  <div className="min-w-0">
                    <p className="text-xs text-gray-700">{ev.message}</p>
                    <p className="text-[11px] text-gray-400">
                      {new Date(ev.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {fullCustomer && (
        <CustomerDetailsModal
          isOpen={showCustomerDetails}
          onClose={() => { setShowCustomerDetails(false); loadFullCustomer(); }}
          customer={fullCustomer}
          onUpdate={loadFullCustomer}
          zIndex={zBase + 60}
        />
      )}

      {showPay && openInvoice && (
        <RecordPaymentModal
          isOpen
          onClose={() => setShowPay(false)}
          onSuccess={async () => {
            setShowPay(false);
            // A paid invoice activates pending staff rentals via DB trigger.
            const { data } = await supabase
              .from('rental_agreements')
              .select('status, started_at, next_bill_date')
              .eq('id', a.id)
              .maybeSingle();
            if (data) setA(prev => ({ ...prev, ...(data as Partial<RentalAgreement>) }));
            toast.success(a.status === 'pending' ? 'Payment received — rental is live' : 'Payment received');
            await refresh();
          }}
          businessId={a.business_id}
          invoiceId={openInvoice.id}
          customerId={a.customer_id}
          bookingTotal={openInvoice.totalCents / 100}
          amountPaid={(openInvoice.totalCents - openInvoice.dueCents) / 100}
        />
      )}
    </SidePanel>
  );
}

/** Compact rentals card for the customer details modal Orders tab. */
export function CustomerRentalsCard({ businessId, customerId, zBase = 100160 }: {
  businessId: string;
  customerId: string;
  zBase?: number;
}) {
  const [rentals, setRentals] = useState<RentalAgreement[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [selected, setSelected] = useState<RentalAgreement | null>(null);
  const [showStart, setShowStart] = useState(false);
  const [hasCard, setHasCard] = useState<boolean | null>(null);

  const load = useCallback(() => {
    listRentalAgreements(businessId, customerId)
      .then(setRentals)
      .catch(() => {})
      .finally(() => setLoaded(true));
    fetchCustomersWithCards(businessId, [customerId])
      .then(set => setHasCard(set.has(customerId)))
      .catch(() => {});
  }, [businessId, customerId]);

  useEffect(() => { load(); }, [load]);

  const visible = rentals.filter(r => r.status !== 'ended' && r.status !== 'canceled');
  if (!loaded) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2">
          <CalendarClock className="h-4 w-4 text-indigo-500" /> Rentals
          {visible.length > 0 && (
            <span className="text-xs font-normal text-gray-500">
              · {visible.reduce((s, r) => s + r.quantity, 0)} unit{visible.reduce((s, r) => s + r.quantity, 0) !== 1 ? 's' : ''} out
            </span>
          )}
        </h4>
        <button
          onClick={() => setShowStart(true)}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-semibold"
        >
          <Plus className="h-3.5 w-3.5" /> Start rental
        </button>
      </div>
      {visible.length === 0 ? (
        <p className="text-xs text-gray-400">
          {rentals.length > 0
            ? `No active rentals — ${rentals.length} past rental${rentals.length !== 1 ? 's' : ''}.`
            : 'No rentals yet.'}
        </p>
      ) : (
        <div className="space-y-2">
          {visible.map(a => (
            <RentalRow key={a.id} a={a} hideCustomer noCard={hasCard === false} onClick={() => setSelected(a)} />
          ))}
        </div>
      )}
      {selected && (
        <RentalDetailPanel
          agreement={selected}
          onClose={() => setSelected(null)}
          onChanged={load}
          zBase={zBase}
        />
      )}
      {showStart && (
        <StartRentalModal
          businessId={businessId}
          presetCustomerId={customerId}
          onClose={() => setShowStart(false)}
          onStarted={load}
          zBase={zBase + 40}
        />
      )}
    </div>
  );
}

type Filter = 'live' | 'past_due' | 'pending' | 'ended' | 'all';

const FILTERS: { key: Filter; label: string }[] = [
  { key: 'live', label: 'Active' },
  { key: 'past_due', label: 'Past due' },
  { key: 'pending', label: 'Pending' },
  { key: 'ended', label: 'Ended' },
  { key: 'all', label: 'All' },
];

/** Full Rentals view for the inventory page. */
export default function RentalsTab({ businessId }: { businessId: string }) {
  const [rentals, setRentals] = useState<RentalAgreement[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>('live');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<RentalAgreement | null>(null);
  const [billing, setBilling] = useState(false);
  const [showStart, setShowStart] = useState(false);
  const [cardCustomers, setCardCustomers] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    try {
      const list = await listRentalAgreements(businessId);
      const cards = await fetchCustomersWithCards(businessId, list.map(a => a.customer_id)).catch(() => null);
      setRentals(list);
      if (cards) setCardCustomers(cards);
      else setCardCustomers(new Set(list.map(a => a.customer_id))); // lookup failed — hide badges rather than mislabel
    } catch (err) {
      console.error('Error loading rentals:', err);
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rentals.filter(a => {
      const statusOk = (() => {
        switch (filter) {
          case 'live': return a.status === 'active' || a.status === 'past_due' || a.status === 'pending';
          case 'past_due': return a.status === 'past_due';
          case 'pending': return a.status === 'pending';
          case 'ended': return a.status === 'ended' || a.status === 'canceled';
          default: return true;
        }
      })();
      if (!statusOk) return false;
      if (!q) return true;
      const hay = [a.rental_number, customerName(a), a.customer?.company, a.item?.name]
        .filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [rentals, filter, search]);

  const live = rentals.filter(a => a.status === 'active' || a.status === 'past_due');
  const unitsOut = live.reduce((s, a) => s + a.quantity, 0);
  const monthlyCents = live.reduce(
    (s, a) => s + a.rate_cents * a.quantity * (a.billing_interval === 'week' ? 4.33 : 1), 0);
  const pastDueCount = rentals.filter(a => a.status === 'past_due').length;
  const dueNow = live.some(a => a.next_bill_date && a.next_bill_date <= new Date().toISOString().slice(0, 10));

  const runBilling = async () => {
    setBilling(true);
    try {
      const res = await runRentalBilling(businessId);
      const billed = (res.outcomes || []).filter(o => o.result !== 'no_bill' && o.result !== 'error');
      if (billed.length === 0) {
        toast.success('Nothing due to bill right now');
      } else {
        toast.success(`Billing run complete — ${billed.length} invoice${billed.length > 1 ? 's' : ''} processed`);
      }
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Billing run failed');
    } finally {
      setBilling(false);
    }
  };

  return (
    <div>
      {/* Header stats */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex flex-wrap gap-3">
          <div className="bg-white border border-gray-200 rounded-xl px-4 py-2.5">
            <p className="text-[11px] text-gray-500 font-medium">Units on rent</p>
            <p className="text-lg font-bold text-gray-900">{unitsOut}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl px-4 py-2.5">
            <p className="text-[11px] text-gray-500 font-medium">Recurring / month</p>
            <p className="text-lg font-bold text-gray-900">{money(Math.round(monthlyCents))}</p>
          </div>
          {pastDueCount > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-2.5">
              <p className="text-[11px] text-red-600 font-medium">Past due</p>
              <p className="text-lg font-bold text-red-700">{pastDueCount}</p>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowStart(true)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold"
          >
            <Plus className="h-4 w-4" /> Start rental
          </button>
          <button
            onClick={runBilling}
            disabled={billing}
            title={dueNow ? 'Bill everything due now' : 'Nothing due — runs automatically every day'}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${billing ? 'animate-spin' : ''}`} />
            {billing ? 'Billing…' : 'Run billing now'}
          </button>
        </div>
      </div>

      {/* Search + filters */}
      <div className="relative max-w-3xl mb-3">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search rental # (R-0001), customer, item"
          className="w-full pl-9 pr-3 py-2.5 bg-gray-100 border-0 rounded-[10px] text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white"
        />
      </div>
      <div className="flex gap-1.5 mb-4 overflow-x-auto pb-1">
        {FILTERS.map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
              filter === f.key ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="h-40 flex items-center justify-center text-sm text-gray-400">Loading rentals…</div>
      ) : filtered.length === 0 ? (
        <div className="h-48 flex flex-col items-center justify-center text-center">
          <PackageOpen className="h-10 w-10 text-gray-300 mb-3" />
          <p className="text-sm font-medium text-gray-600">No rentals here</p>
          <p className="text-xs text-gray-400 mt-1 max-w-xs">
            Turn on “Rent this product” on an item — rentals start automatically at online checkout, or start one yourself with the Start rental button.
          </p>
        </div>
      ) : (
        <div className="space-y-2 max-w-3xl">
          {filtered.map(a => (
            <RentalRow key={a.id} a={a} noCard={!cardCustomers.has(a.customer_id)} onClick={() => setSelected(a)} />
          ))}
        </div>
      )}

      {selected && (
        <RentalDetailPanel
          agreement={selected}
          onClose={() => setSelected(null)}
          onChanged={load}
        />
      )}
      {showStart && (
        <StartRentalModal
          businessId={businessId}
          onClose={() => setShowStart(false)}
          onStarted={load}
        />
      )}
    </div>
  );
}
